/**
 * Forget with undo (M5.3). The row leaves the list only after the server said 2xx; a failed
 * delete keeps the row and says "Not forgotten. Try again."; Undo is offered for exactly
 * UNDO_MS and restores the row at its old place after the server said 2xx. The store is pure
 * state so the screen can render a toast that never shifts the list under the finger.
 */

export const UNDO_MS = 5000;

export interface Pending<T> {
  item: T;
  index: number;
}

export interface MemoryUndoState<T> {
  items: T[];
  /** The last forgotten row while its undo window is open. */
  pending: Pending<T> | null;
  /** 'not_forgotten' after a failed delete; 'not_restored' after a failed undo. */
  error: 'not_forgotten' | 'not_restored' | null;
  /** Ids of rows with a request in flight. */
  busy: string | null;
}

export interface MemoryUndoDeps {
  forget: (id: string) => Promise<unknown>;
  restore: (id: string) => Promise<unknown>;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

export class MemoryUndoStore<T extends { memory_id: string }> {
  private state: MemoryUndoState<T>;
  private readonly listeners = new Set<() => void>();
  private timer: unknown;

  constructor(
    items: T[],
    private readonly deps: MemoryUndoDeps,
  ) {
    this.state = { items, pending: null, error: null, busy: null };
  }

  getSnapshot = (): MemoryUndoState<T> => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Replace the list (after a load or an edit) without touching the undo window. */
  setItems(items: T[]): void {
    this.set({ items });
  }

  /** Forget a row: the row goes only after 2xx; Undo opens for exactly UNDO_MS. */
  async forget(item: T): Promise<boolean> {
    if (this.state.busy) return false;
    this.set({ busy: item.memory_id, error: null });
    try {
      await this.deps.forget(item.memory_id);
    } catch {
      this.set({ busy: null, error: 'not_forgotten' });
      return false;
    }
    const index = this.state.items.findIndex((x) => x.memory_id === item.memory_id);
    this.stopTimer();
    this.set({
      busy: null,
      items: this.state.items.filter((x) => x.memory_id !== item.memory_id),
      pending: { item, index: Math.max(0, index) },
    });
    this.timer = (this.deps.setTimeout ?? setTimeout)(() => this.expire(), UNDO_MS);
    return true;
  }

  /** Undo the last forget: the row returns to its place after 2xx. */
  async undo(): Promise<boolean> {
    const pending = this.state.pending;
    if (!pending || this.state.busy) return false;
    this.stopTimer();
    this.set({ pending: null, busy: pending.item.memory_id, error: null });
    try {
      await this.deps.restore(pending.item.memory_id);
    } catch {
      this.set({ busy: null, error: 'not_restored' });
      return false;
    }
    const items = [...this.state.items];
    items.splice(Math.min(pending.index, items.length), 0, pending.item);
    this.set({ busy: null, items });
    return true;
  }

  /** The undo window closed: the forget stands. */
  expire(): void {
    this.stopTimer();
    if (this.state.pending) this.set({ pending: null });
  }

  dismissError(): void {
    if (this.state.error) this.set({ error: null });
  }

  dispose(): void {
    this.stopTimer();
    this.listeners.clear();
  }

  private stopTimer(): void {
    if (this.timer !== undefined) {
      (this.deps.clearTimeout ?? clearTimeout)(this.timer as never);
      this.timer = undefined;
    }
  }

  private set(patch: Partial<MemoryUndoState<T>>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }
}
